import { isEmpty, trim } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import useDebounce from "react-use/lib/useDebounce";
import { SkillFragment, useGetSkillsLazyQuery, useInsertSkill } from '../../graphql/generated/definitions';
import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import SearchInput from "../utils/SearchInput";
import SearchPopover from "../utils/SearchPopover";
import SearchPopoverHeading from "../utils/SearchPopoverHeading";
import SearchPopoverItem from "../utils/SearchPopoverItem";

const SkillItem: React.FC<{
  skill: SkillFragment;
  emphasized?: boolean;
  baseId?: string;
}> = ({ skill, baseId, emphasized = false }) => (
  <SearchPopoverItem
    id={`${baseId}${skill.id}${emphasized ? "-emphasized" : ""}`}
  >
    <p>{skill?.name}</p>
  </SearchPopoverItem>
);

interface SkillSearchPopoverProps {
  handleItemSelected: (item: SkillFragment) => void;
  defaultValue?: string;
  baseId?: string;
}

const SkillSearchPopover: React.FC<SkillSearchPopoverProps> = ({
  handleItemSelected,
  defaultValue = "",
  baseId = ""
}) => {
  const t = useTranslations();
  const [isPopoverOpen, togglePopover] = useState(false);
  const [search, setSearch] = useState("");

  const { isCapableOfInsertingSkills, locale, companyId } = useViewer();

  const [
    getSkills,
    { data: skillsData, loading: skillsLoading },
  ] = useGetSkillsLazyQuery({
    variables: {
      locale,
    },
  });
  const skills = skillsData?.skills.collection || [];

  const [insertSkill] = useInsertSkill();

  const { notifySuccess } = useNotification();

  // We don't want to show the "Add XXX" button
  // when we have an exact match.
  const isMatchExact = useMemo(
    () => skills.find(({ name }) => name === trim(search)),
    [search, skills]
  );

  const _handleItemSelected = useCallback(
    (item: SkillFragment) => {
      handleItemSelected(item);
      setSearch(item.name);
      togglePopover(false);
    },
    [handleItemSelected, setSearch, togglePopover]
  );

  const handleCreateSkill = useCallback(async () => {
    const result = await insertSkill({
      variables: {
        input: {
          name: search,
          companyId
        },
      },
    });

    notifySuccess({
      title: t(`skills.notification.success.create.title`) as string,
    });

    _handleItemSelected(result.data.insertSkill.skill);
  }, [insertSkill, search]);

  const handleOpenPopover = useCallback(() => {
    togglePopover(true);
  }, [togglePopover]);

  const handleClosePopover = useCallback(() => {
    togglePopover(false);
  }, [togglePopover]);

  useDebounce(
    () => {
      // Avoid unecessary fetch when the question isn't of type skill & avoid early fetching
      if (isPopoverOpen)
        getSkills({
          variables: { q: search, sortBy: "length", per: 40, locale },
        });
    },
    500,
    [search, locale, isPopoverOpen]
  );

  return (
    <SearchPopover
      title={<SearchPopoverHeading>{t("skills.title")}</SearchPopoverHeading>}
      onItemSelected={_handleItemSelected}
      hasSearch={false}
      open={isPopoverOpen}
      button={
        <div>
          <SearchInput
            liveSearch
            id={`${baseId}skillSearch`}
            onFocus={handleOpenPopover}
            onBlur={handleClosePopover}
            placeholder={t("skills.searchPlaceholder") as string}
            value={search || defaultValue}
            onChange={(e) => {
              if (!isPopoverOpen) {
                handleOpenPopover();
              }
              setSearch(e);
            }}
          />
        </div>
      }
      placeholder={t("skills.searchPlaceholder") as string}
      items={skills}
      loading={!skillsData || skillsLoading}
      renderItem={(item: SkillFragment) => (
        <SkillItem skill={item} baseId={"skill"} />
      )}
      noMatch={
        !isCapableOfInsertingSkills && (
          <SearchPopoverItem>
            {t("wombat.select.emptyResult")}
          </SearchPopoverItem>
        )
      }
      footer={
        isCapableOfInsertingSkills &&
        !isEmpty(search) &&
        !isMatchExact && (
          <SearchPopoverItem
            className="cursor-pointer"
            onClick={handleCreateSkill}
            id={`${baseId}createSkillButton`}
          >
            {t("skills.add", { skillName: search })}
          </SearchPopoverItem>
        )
      }
    />
  );
};

export default SkillSearchPopover;
